import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { authClient } from "@/lib/auth";

const PASSWORD_RESET_REDIRECT_PATH = "/reset-password";
const EMAIL_VERIFICATION_AFTER_SIGNIN_MESSAGE = "Your email is not verified yet. We sent a fresh verification link to your inbox.";
// Email+password account creation is disabled server-side (see apps/api/src/auth.ts:
// emailAndPassword.disableSignUp). New users sign up with Google/GitHub; the
// email+password form here is sign-in only, for existing users.
const SIGNUP_SOCIAL_ONLY_MESSAGE = "Create your Cord account with Google or GitHub below.";
// BetterAuth's OAuth Provider redirects unauthenticated authorization requests
// to this app login page with a short-lived signed query. After login we must
// resubmit that exact query to the API authorization endpoint before its default
// ten-minute expiry, otherwise Gitea's OIDC flow is lost.
const BETTER_AUTH_OAUTH_AUTHORIZE_PATH = "/api/auth/oauth2/authorize";

export const Route = createFileRoute("/login")({
  component: LoginPage,
  validateSearch: (search: Record<string, unknown>) => {
    // Only allow relative paths for redirect to prevent open redirect attacks
    let redirect: string | undefined;
    if (typeof search.redirect === "string" && search.redirect.startsWith("/") && !search.redirect.startsWith("//")) {
      redirect = search.redirect;
    }
    return {
      redirect,
      mode: search.mode === "signup" ? ("signup" as const) : undefined,
      email: typeof search.email === "string" ? search.email : undefined,
    };
  },
});

function LoginPage() {
  const { data: session, isPending } = authClient.useSession();
  const navigate = useNavigate();
  const { redirect, mode: initialMode, email: initialEmail } = Route.useSearch();

  const [mode, setMode] = useState<"signin" | "signup">(initialMode ?? "signin");
  const [email, setEmail] = useState(initialEmail ?? "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetEmailPending, setResetEmailPending] = useState(false);

  const resumeOAuthAuthorizationIfPresent = (): boolean => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has("sig") || !params.has("client_id") || !params.has("redirect_uri")) {
      return false;
    }
    const baseUrl = import.meta.env.VITE_API_URL || window.location.origin;
    window.location.href = `${baseUrl}${BETTER_AUTH_OAUTH_AUTHORIZE_PATH}${window.location.search}`;
    return true;
  };

  if (session?.user && !isPending) {
    if (resumeOAuthAuthorizationIfPresent()) return null;
    navigate({ to: redirect || "/" });
    return null;
  }

  const requestPasswordResetLink = async () => {
    const normalizedEmail = email.trim();
    if (!normalizedEmail) {
      setError("Enter your email first.");
      return;
    }

    setResetEmailPending(true);
    setError("");
    try {
      const redirectTo = new URL(PASSWORD_RESET_REDIRECT_PATH, window.location.origin).toString();
      const result = await authClient.requestPasswordReset({
        email: normalizedEmail,
        redirectTo,
      });

      if (result.error) {
        setError(result.error.message ?? "Failed to send password reset email.");
        return;
      }

      setNotice("If an eligible account exists for this email, we sent a password reset link. You can also sign in with Google using the same email.");
      setMode("signin");
      setPassword("");
    } catch {
      setError("Failed to send password reset email.");
    } finally {
      setResetEmailPending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setNotice("");
    setLoading(true);

    try {
      // Email+password is sign-in only; account creation is social-only.
      const res = await authClient.signIn.email({
        email,
        password,
      });
      if (res.error) {
        const message = res.error.message ?? "Sign in failed";
        if (message.toLowerCase().includes("verif")) {
          setNotice(EMAIL_VERIFICATION_AFTER_SIGNIN_MESSAGE);
        }
        setError(message);
        setLoading(false);
        return;
      }
      if (!resumeOAuthAuthorizationIfPresent()) {
        navigate({ to: redirect || "/" });
      }
    } catch {
      setError("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  const inputClassName =
    "w-full rounded-xl border border-white/[0.06] bg-white/[0.03] px-4 py-3 text-[14px] text-zinc-100 placeholder-zinc-500 outline-none transition-all duration-200 focus:border-white/[0.12] focus:bg-white/[0.05] focus:ring-1 focus:ring-white/[0.08]";

  return (
    <div className="noise-overlay relative flex min-h-screen items-center justify-center bg-[#09090b] overflow-hidden">
      {/* Background: single very faint warm glow — barely visible, just breaks the flat black */}
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute left-1/2 top-[35%] size-[700px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-zinc-500/[0.03] blur-[150px]" />
      </div>

      <div className="relative w-full max-w-[400px] px-6">
        {/* Card */}
        <div
          className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8 shadow-2xl shadow-black/40 backdrop-blur-xl"
          style={{
            animation: "hero-reveal 0.5s cubic-bezier(0.16, 1, 0.3, 1) both",
          }}
        >
          {/* Logo */}
          <div className="mb-8 flex flex-col items-center">
            <div className="relative mb-5">
              <div className="absolute inset-0 rounded-2xl bg-white/[0.06] blur-xl" />
              <div className="relative size-14 overflow-hidden rounded-2xl shadow-lg ring-1 ring-white/[0.08]">
                <img src="/cord-logo.svg" alt="Cord" className="size-full" />
              </div>
            </div>
            <h1 className="text-[22px] font-semibold tracking-[-0.02em] text-white">
              Welcome to Cord
            </h1>
            <p className="mt-2 text-center text-[14px] leading-relaxed text-zinc-500">
              Your AI coding agent, running in the cloud.
            </p>
          </div>

          {/* Email/password form */}
          {mode === "signin" && (
          <form onSubmit={handleSubmit} className="space-y-3">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (notice) setNotice(""); }}
              required
              className={inputClassName}
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); if (notice) setNotice(""); }}
              required
              minLength={8}
              className={inputClassName}
            />

            {notice && (
              <p className="text-center text-sm font-medium text-amber-300/90">{notice}</p>
            )}

            {error && (
              <p className="text-center text-sm font-medium text-red-400/90">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-xl bg-white px-4 py-3 text-[14px] font-semibold text-zinc-950 transition-all duration-200 hover:bg-zinc-200 focus:outline-none focus:ring-2 focus:ring-white/30 focus:ring-offset-2 focus:ring-offset-zinc-900 active:scale-[0.98] disabled:opacity-50"
            >
              {loading ? "Signing in..." : "Sign in"}
            </button>

            <button
              type="button"
              onClick={() => void requestPasswordResetLink()}
              disabled={loading || resetEmailPending}
              className="w-full text-center text-sm font-medium text-zinc-400 transition-colors hover:text-zinc-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {resetEmailPending
                ? "Sending reset link..."
                : "Forgot password or using a Slack-created account? Email me a reset link"}
            </button>
          </form>
          )}

          {mode === "signup" && (
            <p className="mb-2 text-center text-sm text-zinc-400">
              {SIGNUP_SOCIAL_ONLY_MESSAGE}
            </p>
          )}

          <p className="mt-4 text-center text-sm text-zinc-500">
            {mode === "signin" ? (
              <>
                Don&apos;t have an account?{" "}
                <button
                  onClick={() => { setMode("signup"); setError(""); setNotice(""); }}
                  className="font-medium text-zinc-300 transition-colors hover:text-white"
                >
                  Sign up
                </button>
              </>
            ) : (
              <>
                Already have an account?{" "}
                <button
                  onClick={() => { setMode("signin"); setError(""); setNotice(""); }}
                  className="font-medium text-zinc-300 transition-colors hover:text-white"
                >
                  Sign in
                </button>
              </>
            )}
          </p>

          {/* Divider — only meaningful in sign-in mode, above the social options */}
          {mode === "signin" && (
            <div className="my-6 flex items-center gap-4">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />
              <span className="text-[12px] font-medium uppercase tracking-widest text-zinc-600">or</span>
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />
            </div>
          )}

          {/* Social OAuth buttons */}
          <div className="flex flex-col gap-2.5">
            <button
              onClick={() =>
                authClient.signIn.social({
                  provider: "github",
                  callbackURL: window.location.origin + (redirect || "/"),
                })
              }
              className="flex w-full items-center justify-center gap-2.5 rounded-xl border border-white/[0.06] bg-white/[0.03] px-4 py-3 text-[14px] font-medium text-zinc-300 transition-all duration-200 hover:border-white/[0.1] hover:bg-white/[0.06] hover:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-zinc-400/30 focus:ring-offset-2 focus:ring-offset-zinc-900 active:scale-[0.98]"
            >
              <svg className="size-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
              </svg>
              Continue with GitHub
            </button>

            <button
              onClick={() =>
                authClient.signIn.social({
                  provider: "google",
                  callbackURL: window.location.origin + (redirect || "/"),
                })
              }
              className="flex w-full items-center justify-center gap-2.5 rounded-xl border border-white/[0.06] bg-white/[0.03] px-4 py-3 text-[14px] font-medium text-zinc-300 transition-all duration-200 hover:border-white/[0.1] hover:bg-white/[0.06] hover:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-zinc-400/30 focus:ring-offset-2 focus:ring-offset-zinc-900 active:scale-[0.98]"
            >
              <img src="/google.svg" alt="Google" className="size-5" />
              Continue with Google
            </button>
          </div>

          <p className="mt-6 text-center text-xs leading-relaxed text-zinc-600">
            By signing in, you agree to grant repository access for your coding sessions.
          </p>
        </div>
      </div>
    </div>
  );
}
